import { useTranslation } from 'react-i18next'

import * as C from '@/game/constants'
import { useGame, type RunResult } from '@/game/useGame'

import styles from './play.module.scss'

export interface PlayScreenProps {
  name: string
  muted: boolean
  onToggleMute: () => void
  onCrash: (result: RunResult) => void
}

export default function PlayScreen({ name, muted, onToggleMute, onCrash }: PlayScreenProps) {
  const { t } = useTranslation()
  const { canvasRef, stageRef, hud, bubble, countdown } = useGame({ muted, onCrash })

  const autopilotRatio = hud.autopilot / C.AUTOPILOT_TIME
  const doubleRatio = hud.doublePoints / C.DOUBLE_POINTS_TIME

  return (
    <div
      className={styles.stage}
      ref={stageRef}
    >
      <canvas
        className={styles.canvas}
        ref={canvasRef}
      />

      <div className={styles.hud}>
        <div className={styles.topRow}>
          <div className={styles.scoreBlock}>
            <span className={styles.scoreLabel}>{t('hud.score')}</span>
            <span className={styles.score}>{hud.score.toLocaleString('pl-PL')}</span>
          </div>

          <div className={styles.rightStack}>
            <button
              className={styles.mute}
              data-ui
              type='button'
              onClick={onToggleMute}
              aria-label={muted ? t('common.soundOn') : t('common.soundOff')}
            >
              {muted ?
                <MutedIcon />
              : <SoundIcon />}
            </button>
            <div className={styles.speed}>
              <strong>{hud.speed}</strong>
              <span>{t('hud.speed')}</span>
            </div>
          </div>
        </div>

        <div className={styles.chips}>
          {hud.combo > 0 && (
            <span className={styles.combo}>
              {t('hud.combo')} ×{hud.multiplier.toFixed(1)}
            </span>
          )}
          {hud.autopilot > 0 && (
            <span className={`${styles.timer} ${styles.bobi}`}>
              <i style={{ transform: `scaleX(${autopilotRatio})` }} />
              {t('hud.autopilot')}
            </span>
          )}
          {hud.doublePoints > 0 && (
            <span className={`${styles.timer} ${styles.sokar}`}>
              <i style={{ transform: `scaleX(${doubleRatio})` }} />
              {t('hud.doubled')}
            </span>
          )}
        </div>
      </div>

      {bubble && (
        <div
          className={styles.bubble}
          key={bubble.id}
        >
          <span className={styles.bubbleAvatar}>B</span>
          <p>{t(bubble.key)}</p>
        </div>
      )}

      {countdown !== null && (
        <div className={styles.countdown}>
          <span className={styles.driver}>{name}</span>
          <strong key={countdown}>{countdown}</strong>
          <p>{t('bubble.ready')}</p>
        </div>
      )}

      {countdown === null && hud.score < 400 && <p className={styles.hint}>{t('start.howLane')}</p>}
    </div>
  )
}

function SoundIcon() {
  return (
    <svg
      viewBox='0 0 24 24'
      width='20'
      height='20'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
    >
      <path d='M11 5 6 9H3v6h3l5 4z' />
      <path d='M16 8a5 5 0 0 1 0 8' />
      <path d='M19 5a9 9 0 0 1 0 14' />
    </svg>
  )
}

function MutedIcon() {
  return (
    <svg
      viewBox='0 0 24 24'
      width='20'
      height='20'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
    >
      <path d='M11 5 6 9H3v6h3l5 4z' />
      <path d='m17 9 4 6M21 9l-4 6' />
    </svg>
  )
}
